import React from 'react';
import type { Profile } from '../types';
import { Users, AlertTriangle, Flame } from 'lucide-react';

interface Props {
  patients: Profile[];
}

const TherapistAggregateInsights: React.FC<Props> = ({ patients }) => {
  if (!patients || patients.length === 0) return null;

  // חישוב מטופלים פעילים (נראו ב-7 ימים האחרונים)
  const now = new Date();
  const activePatients = patients.filter(p => {
    if (!p.last_seen) return false;
    const diffTime = Math.abs(now.getTime() - new Date(p.last_seen).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  });

  // חישוב מטופלים בסיכון גבוה (לא נראו מעל 4 ימים או שאיבדו רצף)
  const atRiskPatients = patients.filter(p => {
    if (!p.last_seen) return true;
    const diffTime = Math.abs(now.getTime() - new Date(p.last_seen).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 4;
  });

  // ממוצע רצף (Streak)
  const totalStreak = patients.reduce((acc, p) => acc + (p.streak || 0), 0);
  const avgStreak = patients.length > 0 ? (totalStreak / patients.length).toFixed(1) : '0';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <div className="bg-muted p-4 rounded-2xl border border-border shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-primary/15 p-2 rounded-lg">
            <Users size={20} className="text-primary" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">פעילים שבועית</span>
        </div>
        <div className="text-2xl font-bold text-foreground">
          {activePatients.length} <span className="text-sm font-normal text-muted-foreground">/ {patients.length}</span>
        </div>
      </div>

      <div className="bg-muted p-4 rounded-2xl border border-border shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-orange-500/20 p-2 rounded-lg">
            <Flame size={20} className="text-orange-400" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">ממוצע רצף</span>
        </div>
        <div className="text-2xl font-bold text-foreground">
          {avgStreak} <span className="text-sm font-normal text-muted-foreground">ימים</span>
        </div>
      </div>

      <div className="bg-muted p-4 rounded-2xl border border-border shadow-lg col-span-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="bg-red-500/20 p-2 rounded-lg">
              <AlertTriangle size={20} className="text-red-400" />
            </div>
            <span className="text-sm text-muted-foreground font-medium">מטופלים דורשי תשומת לב</span>
          </div>
          <span className="text-xs font-bold px-2 py-1 bg-red-500/10 text-red-400 rounded-md">
            {atRiskPatients.length} מטופלים
          </span>
        </div>
        
        {atRiskPatients.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-3">
            {atRiskPatients.map(p => (
              <div key={p.id} className="text-xs bg-card/50 border border-border px-3 py-1.5 rounded-full flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                <span className="text-muted-foreground">{p.full_name || p.username}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-2">מעולה! אין מטופלים בסיכון נטישה מיידי.</p>
        )}
      </div>
    </div>
  );
};

export default TherapistAggregateInsights;
